import { useState, useEffect, useMemo } from 'react'
import { X } from 'lucide-react'
import type { Patient } from '../../store/patientDetailSlice'
import type { Medication } from '../../store/medicationsSlice'
import type { Veterinarian } from '../../store/settingsSlice'

interface PrescriptionAlert {
  type: 'interaction' | 'allergy'
  severity: 'Warning' | 'Critical'
  description: string
  conflicting_drugs: string
}

interface NewPrescriptionPageModalProps {
  patients: Patient[]
  medications: Medication[]
  veterinarians: Veterinarian[]
  existingPrescriptions: { patient_id: string; medication_name: string; status: string }[]
  onSubmit: (data: {
    patient_id: string
    medication_id: string
    veterinarian_id: string
    dosage: string
    dosage_calculated: number | null
    dose_rate: number | null
    frequency: string
    route: string
    duration: string
    quantity_dispensed: number
    total_refills: number
    start_date: string
    end_date: string
    alerts: PrescriptionAlert[]
    controlled_substance: boolean
    dea_schedule: string | null
  }) => Promise<void>
  onClose: () => void
}

const FREQUENCIES = ['SID', 'BID', 'TID', 'QID', 'q8h', 'q12h', 'PRN'] as const

export function NewPrescriptionPageModal({
  patients,
  medications,
  veterinarians,
  existingPrescriptions,
  onSubmit,
  onClose,
}: NewPrescriptionPageModalProps) {
  const [patientId, setPatientId] = useState('')
  const [patientSearch, setPatientSearch] = useState('')
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false)
  const [medicationId, setMedicationId] = useState('')
  const [medSearch, setMedSearch] = useState('')
  const [medDropdownOpen, setMedDropdownOpen] = useState(false)
  const [vetId, setVetId] = useState('')
  const [frequencyDropdownOpen, setFrequencyDropdownOpen] = useState(false)
  const [vetDropdownOpen, setVetDropdownOpen] = useState(false)
  const [dosage, setDosage] = useState('')
  const [dosageCalculated, setDosageCalculated] = useState<number | null>(null)
  const [doseRate, setDoseRate] = useState<number | null>(null)
  const [frequency, setFrequency] = useState('')
  const [route, setRoute] = useState('Oral')
  const [duration, setDuration] = useState('')
  const [quantity, setQuantity] = useState('')
  const [totalRefills, setTotalRefills] = useState('0')
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10))
  const [endDate, setEndDate] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [alertsAcknowledged, setAlertsAcknowledged] = useState(false)

  const activePatients = useMemo(
    () => patients.filter((p) => p.status === 'Active'),
    [patients]
  )

  const filteredPatients = useMemo(() => {
    if (!patientSearch) return activePatients
    const q = patientSearch.toLowerCase()
    return activePatients.filter((p) => p.name.toLowerCase().includes(q))
  }, [activePatients, patientSearch])

  const filteredMeds = useMemo(() => {
    if (!medSearch) return medications
    const q = medSearch.toLowerCase()
    return medications.filter((m) => m.name.toLowerCase().includes(q))
  }, [medications, medSearch])

  const selectedPatient = patients.find((p) => p.id === patientId)
  const selectedMedication = medications.find((m) => m.id === medicationId)

  // Auto-calculate dosage when patient and medication are selected
  useEffect(() => {
    if (selectedPatient && selectedMedication && selectedMedication.default_dose_rate) {
      const rate = selectedMedication.default_dose_rate
      let weightKg = selectedPatient.weight || 0
      if (selectedPatient.weight_unit === 'lbs') {
        weightKg = weightKg / 2.205
      }
      const calculated = rate * weightKg
      const rounded = Math.round(calculated * 100) / 100
      setDosageCalculated(rounded)
      setDoseRate(rate)
      setDosage(`${rounded} mg`)
    }
  }, [selectedPatient, selectedMedication])

  // Build dosage calculation display string
  const dosageCalcDisplay = useMemo(() => {
    if (!selectedPatient || !selectedMedication || !selectedMedication.default_dose_rate) return null
    const rate = selectedMedication.default_dose_rate
    let weightKg = selectedPatient.weight || 0
    const isLbs = selectedPatient.weight_unit === 'lbs'
    const originalWeight = selectedPatient.weight || 0
    if (isLbs) {
      weightKg = originalWeight / 2.205
    }
    const calculated = rate * weightKg
    const rounded = Math.round(calculated * 100) / 100
    const weightKgRounded = Math.round(weightKg * 100) / 100
    if (isLbs) {
      return `${originalWeight} lbs / 2.205 = ${weightKgRounded} kg → ${rate} mg/kg × ${weightKgRounded} kg = ${rounded} mg`
    }
    return `${rate} mg/kg × ${weightKg} kg = ${rounded} mg`
  }, [selectedPatient, selectedMedication])

  // Check for drug interactions and allergies
  const alerts = useMemo<PrescriptionAlert[]>(() => {
    if (!patientId || !medicationId || !selectedPatient || !selectedMedication) return []
    const result: PrescriptionAlert[] = []

    // Check patient allergies against medication
    if (selectedPatient.allergies) {
      const patientAllergies = selectedPatient.allergies.toLowerCase().split(',').map((a) => a.trim())
      const medName = selectedMedication.name.toLowerCase()
      const medCategory = selectedMedication.category.toLowerCase()

      for (const allergy of patientAllergies) {
        if (!allergy) continue
        // Check if medication name or category matches allergy
        if (
          medName.includes(allergy) ||
          allergy.includes(medName) ||
          medCategory.includes(allergy) ||
          allergy.includes(medCategory) ||
          // Penicillin family check (Amoxicillin is in penicillin family)
          (allergy === 'penicillin' && ['amoxicillin', 'ampicillin', 'penicillin'].includes(medName))
        ) {
          result.push({
            type: 'allergy',
            severity: 'Critical',
            description: `Patient has documented ${selectedPatient.allergies} allergy. ${selectedMedication.name} may cause an allergic reaction.`,
            conflicting_drugs: `${selectedMedication.name}, ${selectedPatient.allergies}`,
          })
          break
        }
      }
    }

    // Helper: match interaction term against a medication's name/category/notes,
    // handling plural forms (e.g., "NSAIDs" vs "NSAID")
    const matchesInteraction = (term: string, ...texts: string[]): boolean => {
      const termStem = term.replace(/s$/, '')
      for (const text of texts) {
        if (!text) continue
        const t = text.toLowerCase()
        if (t.includes(term) || term.includes(t) || t.includes(termStem)) return true
      }
      return false
    }

    // Check drug interactions with patient's active prescriptions
    const patientActiveMeds = existingPrescriptions
      .filter((rx) => rx.patient_id === patientId && rx.status === 'Active')
      .map((rx) => rx.medication_name)

    if (selectedMedication.known_interactions) {
      const interactions = selectedMedication.known_interactions.toLowerCase().split(',').map((i) => i.trim())
      for (const activeMedName of patientActiveMeds) {
        const activeMed = medications.find((m) => m.name === activeMedName)
        for (const interaction of interactions) {
          if (!interaction) continue
          if (matchesInteraction(interaction, activeMedName.toLowerCase(), activeMed?.category?.toLowerCase() || '', activeMed?.notes?.toLowerCase() || '')) {
            result.push({
              type: 'interaction',
              severity: 'Warning',
              description: `Potential drug interaction between ${selectedMedication.name} and ${activeMedName}.`,
              conflicting_drugs: `${selectedMedication.name}, ${activeMedName}`,
            })
          }
        }
      }
    }

    // Also check reverse: active medication's interactions against new medication
    for (const activeMedName of patientActiveMeds) {
      const med = medications.find((m) => m.name === activeMedName)
      if (med?.known_interactions) {
        const interactions = med.known_interactions.toLowerCase().split(',').map((i) => i.trim())
        for (const interaction of interactions) {
          if (!interaction) continue
          const newMedName = selectedMedication.name.toLowerCase()
          const newMedCategory = selectedMedication.category.toLowerCase()
          const newMedNotes = selectedMedication.notes?.toLowerCase() || ''
          if (matchesInteraction(interaction, newMedName, newMedCategory, newMedNotes)) {
            // Check not already added
            const alreadyAdded = result.some(
              (a) => a.type === 'interaction' && a.conflicting_drugs.includes(activeMedName)
            )
            if (!alreadyAdded) {
              result.push({
                type: 'interaction',
                severity: 'Warning',
                description: `Potential drug interaction between ${activeMedName} and ${selectedMedication.name}.`,
                conflicting_drugs: `${activeMedName}, ${selectedMedication.name}`,
              })
            }
          }
        }
      }
    }

    return result
  }, [patientId, medicationId, selectedPatient, selectedMedication, existingPrescriptions, medications])

  const hasAlerts = alerts.length > 0

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {}
    if (!patientId) newErrors.patient = 'Patient is required'
    if (!medicationId) newErrors.medication = 'Medication is required'
    if (!frequency) newErrors.frequency = 'Frequency is required'
    if (!vetId) newErrors.veterinarian = 'Prescribing Veterinarian is required'
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    if (hasAlerts && !alertsAcknowledged) {
      setErrors({ alerts: 'Please acknowledge alerts before proceeding' })
      return
    }

    await onSubmit({
      patient_id: patientId,
      medication_id: medicationId,
      veterinarian_id: vetId,
      dosage,
      dosage_calculated: dosageCalculated,
      dose_rate: doseRate,
      frequency,
      route,
      duration,
      quantity_dispensed: parseInt(quantity) || 0,
      total_refills: parseInt(totalRefills) || 0,
      start_date: startDate,
      end_date: endDate,
      alerts,
      controlled_substance: selectedMedication?.controlled_substance || false,
      dea_schedule: selectedMedication?.dea_schedule || null,
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      data-testid="new-prescription-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-surface rounded-[8px] shadow-elevation-2 w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[14px] font-semibold text-text-primary">New Prescription</h2>
          <button
            data-testid="new-rx-close-button"
            onClick={onClose}
            className="p-1 rounded hover:bg-hover cursor-pointer"
          >
            <X size={16} className="text-text-muted" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          {/* Patient searchable dropdown */}
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-text-muted">Patient *</span>
            <div className="relative">
              <input
                data-testid="new-rx-patient-search"
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value)
                  setPatientId('')
                  setPatientDropdownOpen(true)
                }}
                onFocus={() => setPatientDropdownOpen(true)}
                placeholder="Search patients..."
                className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
              {patientDropdownOpen && !patientId && (
                <div
                  data-testid="new-rx-patient-dropdown"
                  className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-32 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {filteredPatients.map((p) => (
                    <button
                      key={p.id}
                      data-testid="patient-option"
                      onClick={() => {
                        setPatientId(p.id)
                        setPatientSearch(p.name)
                        setPatientDropdownOpen(false)
                      }}
                      className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {p.name} ({p.species})
                    </button>
                  ))}
                  {filteredPatients.length === 0 && (
                    <p className="px-2 py-1.5 text-[12px] text-text-muted">No patients found</p>
                  )}
                </div>
              )}
            </div>
            {errors.patient && (
              <span data-testid="patient-error" className="text-[11px] text-status-error">{errors.patient}</span>
            )}
          </div>

          {/* Medication searchable dropdown */}
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-text-muted">Medication *</span>
            <div className="relative">
              <input
                data-testid="new-rx-medication-search"
                value={medSearch}
                onChange={(e) => {
                  setMedSearch(e.target.value)
                  setMedicationId('')
                  setMedDropdownOpen(true)
                }}
                onFocus={() => setMedDropdownOpen(true)}
                placeholder="Search medications..."
                className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
              {medDropdownOpen && !medicationId && (
                <div
                  data-testid="new-rx-medication-dropdown"
                  className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-32 overflow-y-auto"
                >
                  {filteredMeds.map((m) => (
                    <button
                      key={m.id}
                      data-testid="medication-option"
                      onClick={() => {
                        setMedicationId(m.id)
                        setMedSearch(m.name)
                        setMedDropdownOpen(false)
                        if (m.common_frequency) {
                          setFrequency(m.common_frequency)
                        }
                      }}
                      className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {m.name} ({m.concentration})
                    </button>
                  ))}
                  {filteredMeds.length === 0 && (
                    <p className="px-2 py-1.5 text-[12px] text-text-muted">No medications found</p>
                  )}
                </div>
              )}
            </div>
            {errors.medication && (
              <span data-testid="medication-error" className="text-[11px] text-status-error">{errors.medication}</span>
            )}
          </div>

          {/* Dosage with auto-calculation */}
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-text-muted">Dosage</span>
            <input
              data-testid="new-rx-dosage"
              value={dosage}
              onChange={(e) => setDosage(e.target.value)}
              placeholder="e.g., 250 mg"
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
            {dosageCalcDisplay && (
              <span data-testid="new-rx-dosage-calculation" className="text-[11px] text-text-muted">
                {dosageCalcDisplay}
              </span>
            )}
          </div>

          {/* Frequency */}
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-text-muted">Frequency *</span>
            <div className="relative">
              <button
                type="button"
                data-testid="new-rx-frequency"
                onClick={() => setFrequencyDropdownOpen(!frequencyDropdownOpen)}
                className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
              >
                {frequency || 'Select frequency...'}
              </button>
              {frequencyDropdownOpen && (
                <div
                  data-testid="new-rx-frequency-dropdown"
                  className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {FREQUENCIES.map((f) => (
                    <button
                      key={f}
                      type="button"
                      data-testid={`frequency-option-${f}`}
                      onClick={() => {
                        setFrequency(f)
                        setFrequencyDropdownOpen(false)
                      }}
                      className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errors.frequency && (
              <span data-testid="frequency-error" className="text-[11px] text-status-error">{errors.frequency}</span>
            )}
          </div>

          {/* Route */}
          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Route of Administration
            <input
              data-testid="new-rx-route"
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              placeholder="e.g., Oral"
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </label>

          {/* Duration */}
          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Duration
            <input
              data-testid="new-rx-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="e.g., 14 days"
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </label>

          {/* Quantity and Refills */}
          <div className="flex gap-3">
            <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
              Quantity Dispensed
              <input
                data-testid="new-rx-quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
              Total Refills Allowed
              <input
                data-testid="new-rx-refills"
                type="number"
                value={totalRefills}
                onChange={(e) => setTotalRefills(e.target.value)}
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
            </label>
          </div>

          {/* Dates */}
          <div className="flex gap-3">
            <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
              Start Date
              <input
                data-testid="new-rx-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
              End Date
              <input
                data-testid="new-rx-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
            </label>
          </div>

          {/* Prescribing Veterinarian */}
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-text-muted">Prescribing Veterinarian *</span>
            <div className="relative">
              <button
                type="button"
                data-testid="new-rx-vet-select"
                onClick={() => setVetDropdownOpen(!vetDropdownOpen)}
                className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
              >
                {veterinarians.find((v) => v.id === vetId)?.name || 'Select vet...'}
              </button>
              {vetDropdownOpen && (
                <div
                  data-testid="new-rx-vet-dropdown"
                  className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {veterinarians.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`vet-option-${v.id}`}
                      onClick={() => {
                        setVetId(v.id)
                        setVetDropdownOpen(false)
                      }}
                      className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errors.veterinarian && (
              <span data-testid="veterinarian-error" className="text-[11px] text-status-error">{errors.veterinarian}</span>
            )}
          </div>

          {/* Alerts section */}
          {hasAlerts && (
            <div data-testid="new-rx-alerts" className="flex flex-col gap-2 mt-2">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  data-testid="new-rx-alert-item"
                  className={`p-2 rounded-[4px] text-[12px] ${
                    alert.severity === 'Critical'
                      ? 'bg-[#f5dede] text-status-error border border-status-error/30'
                      : 'bg-[#f5ecd8] text-status-warning border border-status-warning/30'
                  }`}
                >
                  <span data-testid="new-rx-alert-severity" className="font-medium">
                    {alert.severity === 'Critical' ? '⛔ Critical' : '⚠️ Warning'}:
                  </span>{' '}
                  <span data-testid="new-rx-alert-description">{alert.description}</span>
                </div>
              ))}
              <label className="flex items-center gap-2 text-[12px] text-text-secondary cursor-pointer">
                <input
                  data-testid="new-rx-acknowledge-alerts"
                  type="checkbox"
                  checked={alertsAcknowledged}
                  onChange={(e) => {
                    setAlertsAcknowledged(e.target.checked)
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.alerts
                      return next
                    })
                  }}
                  className="cursor-pointer"
                />
                I acknowledge the above alerts and wish to proceed
              </label>
              {errors.alerts && (
                <span className="text-[11px] text-status-error">{errors.alerts}</span>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="new-rx-cancel-button"
            onClick={onClose}
            className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Cancel
          </button>
          <button
            data-testid="new-rx-submit-button"
            onClick={handleSubmit}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
          >
            Create Prescription
          </button>
        </div>
      </div>
    </div>
  )
}
